import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Calendar, 
  CheckCircle, 
  Star, 
  ChevronRight,
  Menu,
  X,
  Clock,
  Shield,
  Sparkles,
  CreditCard
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";
import ServiceMap from "./components/ServiceMap";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_43e1e462-40c0-4c1d-b4eb-f5b05f46747f/artifacts/k966vfvh_file_00000000e75071fdb95eb1e08046523b.png";

// Service data
const services = [
  {
    icon: Sparkles,
    title: "Weekly Cleanup",
    description: "We visit every week to keep your yard consistently clean and fresh for your furry friends.",
    price: "From $19/week",
  },
  {
    icon: Calendar,
    title: "Double Bagged",
    description: "All waste is double bagged and placed in your trash can. No mess, no hassle, no worries.",
    price: "Hassle-free disposal",
  },
  {
    icon: Shield,
    title: "100% Satisfaction",
    description: "Not happy? We'll come back and make it right. Your satisfaction is our priority.",
    price: "Guaranteed",
  },
];

// Testimonials data
const testimonials = [
  {
    name: "Luke W.",
    date: "April 19, 2026",
    text: "Jon is honest, dependable, and takes real pride in his work. You can trust him to do things right. Highly recommend!",
    rating: 5,
  },
  {
    name: "Bridget",
    date: "April 20, 2026",
    text: "Jon is trustworthy and very professional. He is amazing at what he does and he is a hardworker.",
    rating: 5,
  },
];

// Pricing packages — per-tier pricing for each frequency
const pricingTiers = [
  {
    name: "1 Dog",
    popular: false,
    cleanupLabel: "Perfect for 1 dog",
    pricing: {
      weekly:   { price: "$19", monthlyEst: "Billed monthly ($76/mo)" },
      biweekly: { price: "$30", monthlyEst: "Billed monthly ($60/mo)" },
      monthly:  { price: "$55", monthlyEst: "Billed monthly ($55/mo)" },
    },
  },
  {
    name: "2 Dogs",
    popular: true,
    cleanupLabel: "Perfect for 2 dogs",
    pricing: {
      weekly:   { price: "$23", monthlyEst: "Billed monthly ($92/mo)" },
      biweekly: { price: "$36", monthlyEst: "Billed monthly ($72/mo)" },
      monthly:  { price: "$65", monthlyEst: "Billed monthly ($65/mo)" },
    },
  },
  {
    name: "3 Dogs",
    popular: false,
    cleanupLabel: "Great for 3 dogs",
    pricing: {
      weekly:   { price: "$27", monthlyEst: "Billed monthly ($108/mo)" },
      biweekly: { price: "$42", monthlyEst: "Billed monthly ($84/mo)" },
      monthly:  { price: "$75", monthlyEst: "Billed monthly ($75/mo)" },
    },
  },
  {
    name: "4 Dogs",
    popular: false,
    cleanupLabel: "Great for 4 dogs",
    pricing: {
      weekly:   { price: "$31", monthlyEst: "Billed monthly ($124/mo)" },
      biweekly: { price: "$48", monthlyEst: "Billed monthly ($96/mo)" },
      monthly:  { price: "$85", monthlyEst: "Billed monthly ($85/mo)" },
    },
  },
];

const frequencyOptions = [
  { key: "weekly",   label: "Weekly",     periodLabel: "/visit", cleanupHeadline: "Weekly yard cleanup" },
  { key: "biweekly", label: "Bi-Weekly",  periodLabel: "/visit", cleanupHeadline: "Bi-weekly yard cleanup" },
  { key: "monthly",  label: "Monthly",    periodLabel: "/visit", cleanupHeadline: "Monthly yard cleanup" },
];

// FAQ data
const faqs = [
  {
    q: "Do you clean the yard with the dogs outside?",
    a: "Yes we do — we love being around dogs! However, if your dog has shown any aggression, we ask that you keep them inside during service. The safety of our techs is our number one priority.",
  },
  {
    q: "How do I sign up for service?",
    a: "Easy — just click any \"Sign Up Now!\" button on this site and you'll be taken to our secure registration portal. You'll choose your service frequency, enter your address, and set up payment in a few minutes.",
  },
  {
    q: "Can I cancel or pause my service anytime?",
    a: "Absolutely. There are no long-term contracts — you can pause or cancel your service at any time through your customer portal, no questions asked.",
  },
  {
    q: "Do I need to be home during the cleanup?",
    a: "Nope! As long as we have access to your yard (an unlocked gate or a code), you can go about your day. We'll text you when we're on the way and when we're done.",
  },
  {
    q: "What happens if it rains on my scheduled day?",
    a: "Light rain doesn't stop us — we'll be there. For heavy storms or unsafe conditions, we'll either reschedule to the next available day at no extra charge, or do double-dooty on your next scheduled visit.",
  },
  {
    q: "What do you do with the waste?",
    a: "We double-bag it and place it in your trash bin, or we can leave it double-bagged neatly beside your house — whichever you prefer. We bring our own cleaning supplies and sanitize our tools between every yard.",
  },
  {
    q: "What if my yard hasn't been cleaned in a while?",
    a: "As long as it's not unreasonably bad, we got you — no problem.",
  },
  {
    q: "Can I schedule my own day and time?",
    a: "To keep our routes tight and organized, we schedule the day for you — no guaranteed time, but we'll text you when we're on the way.",
  },
  {
    q: "What if I'm worried you'll leave the gate open?",
    a: "Totally understandable. After every clean we take a picture of your gate closed and post it to your customer portal for your peace of mind.",
  },
  {
    q: "What if I don't see my zip code on your list?",
    a: "We're growing fast! If you're close to our service area, give us a call at (210) 642-9327 or send a message — we may already be able to serve you, or we'll add you to our expansion list.",
  },
];

// Navigation component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <>
      {/* Logo Banner */}
      <div className="bg-navy py-6 text-center">
        <a href="#hero" data-testid="banner-logo">
          <img src={LOGO_URL} alt="Scoopt Logo" className="h-24 md:h-32 w-auto mx-auto" />
        </a>
      </div>
      
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-navy/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-14">
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('services')} className="text-white/80 hover:text-white transition-colors font-dm-sans" data-testid="nav-services">Services</button>
              <button onClick={() => scrollToSection('pricing')} className="text-white/80 hover:text-white transition-colors font-dm-sans" data-testid="nav-pricing">Pricing</button>
              <button onClick={() => scrollToSection('areas')} className="text-white/80 hover:text-white transition-colors font-dm-sans" data-testid="nav-areas">Service Areas</button>
              <button onClick={() => scrollToSection('about')} className="text-white/80 hover:text-white transition-colors font-dm-sans" data-testid="nav-about">About</button>
              <button onClick={() => scrollToSection('reviews')} className="text-white/80 hover:text-white transition-colors font-dm-sans" data-testid="nav-reviews">Reviews</button>
              <button onClick={() => scrollToSection('faq')} className="text-white/80 hover:text-white transition-colors font-dm-sans" data-testid="nav-faq">FAQ</button>
              <a 
                href="https://client.sweepandgo.com/scoopt-irs0l/register"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gradient text-navy font-bold rounded-full px-6 py-2"
                data-testid="nav-contact-btn"
              >
                Sign Up Now!
              </a>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="md:hidden py-4 border-t border-white/10">
              <div className="flex flex-col gap-4">
                <button onClick={() => scrollToSection('services')} className="text-white/80 hover:text-white transition-colors py-2 text-left">Services</button>
                <button onClick={() => scrollToSection('pricing')} className="text-white/80 hover:text-white transition-colors py-2 text-left">Pricing</button>
                <button onClick={() => scrollToSection('areas')} className="text-white/80 hover:text-white transition-colors py-2 text-left">Service Areas</button>
                <button onClick={() => scrollToSection('about')} className="text-white/80 hover:text-white transition-colors py-2 text-left">About</button>
                <button onClick={() => scrollToSection('reviews')} className="text-white/80 hover:text-white transition-colors py-2 text-left">Reviews</button>
                <button onClick={() => scrollToSection('faq')} className="text-white/80 hover:text-white transition-colors py-2 text-left">FAQ</button>
                <a 
                  href="https://client.sweepandgo.com/scoopt-irs0l/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gradient text-navy font-bold rounded-full px-6 py-2.5 w-fit"
                >
                  Sign Up Now!
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="bg-navy min-h-[80vh] flex items-center" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="animate-fade-in-up">
            <span className="inline-block text-coral font-dm-sans font-medium mb-4 tracking-wide">
              STRIVING TO BE SAN ANTONIO'S #1 PET WASTE REMOVAL
            </span>
            <h1 className="font-outfit text-5xl md:text-7xl font-bold text-white tracking-tight mb-6">
              Your Yard,{" "}
              <span className="gradient-text">Spotless</span>
            </h1>
            <p className="text-white/70 text-lg md:text-xl leading-relaxed mb-8 font-dm-sans max-w-lg">
              Professional poop scooping service that gives you back your weekends. 
              Reliable, thorough, and 100% satisfaction guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://client.sweepandgo.com/scoopt-irs0l/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gradient text-navy font-bold rounded-full px-8 py-4 text-lg inline-flex items-center justify-center gap-2"
                  data-testid="hero-cta-btn"
                >
                  Sign Up Now! <ChevronRight size={20} />
                </a>
              <a 
                href="tel:+12106429327"
                className="border-2 border-white text-white font-bold rounded-full px-8 py-4 text-lg inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-all"
                data-testid="hero-phone-btn"
              >
                <Phone size={20} /> Call Now
              </a>
            </div>
            
            {/* Trust badges */}
            <div className="mt-12 flex flex-wrap gap-6">
              <div className="flex items-center gap-2 text-white/60">
                <Shield size={20} className="text-teal-start" />
                <span className="text-sm font-dm-sans">Fully Insured</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Star size={20} className="text-gold" />
                <span className="text-sm font-dm-sans">Locally Owned</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <CheckCircle size={20} className="text-teal-end" />
                <span className="text-sm font-dm-sans">Satisfaction Guaranteed</span>
              </div>
            </div>
          </div>
          
          <div className="relative animate-fade-in-up delay-200">
            <div className="relative z-10">
              <img 
                src="https://images.unsplash.com/photo-1759914915166-01963ecc1ea1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxjdXRlJTIwZG9nJTIwYmFja3lhcmQlMjBncmFzcyUyMGxhd258ZW58MHx8fHwxNzcyNjc3Njk2fDA&ixlib=rb-4.1.0&q=85"
                alt="Cute dog in grassy backyard"
                className="rounded-3xl shadow-2xl w-full object-cover"
                style={{ maxHeight: '500px' }}
              />
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-teal-start to-teal-end rounded-full opacity-20 blur-xl"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-coral rounded-full opacity-20 blur-xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="bg-white py-24 md:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-coral font-dm-sans font-medium tracking-wide">OUR SERVICES</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-navy tracking-tight mt-2">
            How We Keep Your Yard <span className="gradient-text">Clean</span>
          </h2>
          <p className="text-text-light text-lg mt-4 max-w-2xl mx-auto font-dm-sans">
            Choose the service plan that fits your lifestyle. All plans include thorough cleaning and proper waste disposal.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="service-card bg-white rounded-2xl p-8 border border-gray-100 hover:border-teal-start"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-start to-teal-end flex items-center justify-center mb-6">
                <service.icon size={28} className="text-navy" />
              </div>
              <h3 className="font-outfit text-2xl font-bold text-navy mb-3">{service.title}</h3>
              <p className="text-text-light font-dm-sans mb-4">{service.description}</p>
              <p className="text-coral font-outfit font-bold text-lg">{service.price}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const [frequency, setFrequency] = useState('weekly');
  const activeFreq = frequencyOptions.find(f => f.key === frequency);

  return (
    <section id="pricing" className="bg-off-white py-24 md:py-32" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-coral font-dm-sans font-medium tracking-wide">PRICING</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-navy tracking-tight mt-2">
            Simple, Transparent <span className="gradient-text">Pricing</span>
          </h2>
          <p className="text-text-light text-lg mt-4 max-w-2xl mx-auto font-dm-sans">
            No hidden fees. No contracts required. Just clean yards and happy pets.
          </p>
        </div>

        {/* Frequency Toggle */}
        <div className="flex justify-center mb-12" data-testid="frequency-toggle">
          <div className="inline-flex bg-white rounded-full p-1.5 shadow-md border border-gray-100">
            {frequencyOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setFrequency(opt.key)}
                data-testid={`frequency-${opt.key}-btn`}
                className={`px-5 sm:px-7 py-2.5 rounded-full font-dm-sans font-medium text-sm sm:text-base transition-all ${
                  frequency === opt.key
                    ? 'bg-navy text-white shadow-sm'
                    : 'text-navy/70 hover:text-navy'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {pricingTiers.map((tier, index) => {
            const planPricing = tier.pricing[frequency];
            return (
              <div
                key={index}
                className={`bg-white rounded-3xl p-8 shadow-lg relative overflow-hidden ${
                  tier.popular ? 'border-2 border-teal-start ring-4 ring-teal-start/20' : 'border border-gray-100'
                }`}
                data-testid={`pricing-card-${index}`}
              >
                {tier.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-teal-start to-teal-end text-navy text-xs font-bold px-4 py-1 rounded-bl-lg">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="font-outfit text-xl font-bold text-navy mb-2">{tier.name}</h3>
                <p className="text-text-light text-sm mb-4 font-dm-sans" data-testid={`pricing-billing-${index}`}>{planPricing.monthlyEst}</p>
                <div className="mb-6">
                  <span className="font-outfit text-4xl font-bold text-navy" data-testid={`pricing-price-${index}`}>{planPricing.price}</span>
                  <span className="text-text-light font-dm-sans">{activeFreq.periodLabel}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-text-dark font-dm-sans">
                    <CheckCircle size={18} className="text-teal-start flex-shrink-0" />
                    {activeFreq.cleanupHeadline}
                  </li>
                  <li className="flex items-center gap-3 text-text-dark font-dm-sans">
                    <CheckCircle size={18} className="text-teal-start flex-shrink-0" />
                    {tier.cleanupLabel}
                  </li>
                  <li className="flex items-center gap-3 text-text-dark font-dm-sans">
                    <CheckCircle size={18} className="text-teal-start flex-shrink-0" />
                    Double bagged waste
                  </li>
                  <li className="flex items-center gap-3 text-text-dark font-dm-sans">
                    <CheckCircle size={18} className="text-teal-start flex-shrink-0" />
                    Placed in your trash can
                  </li>
                  <li className="flex items-center gap-3 text-text-dark font-dm-sans">
                    <CheckCircle size={18} className="text-teal-start flex-shrink-0" />
                    Cancel anytime
                  </li>
                </ul>
                <a
                  href="https://client.sweepandgo.com/scoopt-irs0l/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full font-bold rounded-full py-3 transition-all text-center block ${
                    tier.popular
                      ? 'btn-gradient text-navy'
                      : 'bg-navy text-white hover:bg-navy-light'
                  }`}
                  data-testid={`pricing-btn-${index}`}
                >
                  Sign Up Now!
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Service Areas Section
const ServiceAreasSection = () => {
  return (
    <section id="areas" className="bg-white py-24 md:py-32" data-testid="areas-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-coral font-dm-sans font-medium tracking-wide">SERVICE AREAS</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-navy tracking-tight mt-2">
            Proudly Serving <span className="gradient-text">San Antonio</span>
          </h2>
          <p className="text-text-light text-lg mt-4 max-w-2xl mx-auto font-dm-sans">
            We currently service the following zip codes. Click on a marker to see area details.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3">
            <ServiceMap />
          </div>
          
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-off-white rounded-2xl p-6">
              <h3 className="font-outfit text-xl font-bold text-navy mb-4">Service Zip Codes</h3>
              <div className="grid grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-1" data-testid="zip-code-list">
                {[
                  { code: "78023", area: "Helotes" },
                  { code: "78108", area: "Cibolo" },
                  { code: "78154", area: "Selma / Schertz" },
                  { code: "78209", area: "Alamo Heights" },
                  { code: "78212", area: "Olmos Park" },
                  { code: "78213", area: "Castle Hills" },
                  { code: "78216", area: "North Central" },
                  { code: "78217", area: "Windcrest" },
                  { code: "78230", area: "North Central" },
                  { code: "78231", area: "Hollywood Park" },
                  { code: "78232", area: "Thousand Oaks" },
                  { code: "78233", area: "Converse N" },
                  { code: "78238", area: "Leon Valley" },
                  { code: "78247", area: "Live Oak" },
                  { code: "78248", area: "Hollywood Park" },
                  { code: "78249", area: "UTSA" },
                  { code: "78250", area: "Hunters Pond" },
                  { code: "78251", area: "Westover Hills" },
                  { code: "78254", area: "Alamo Ranch" },
                  { code: "78255", area: "Shavano Park" },
                  { code: "78256", area: "The Dominion" },
                  { code: "78257", area: "Cordillera Ranch" },
                  { code: "78258", area: "Stone Oak" },
                  { code: "78259", area: "Encino Park" },
                  { code: "78260", area: "Timberwood Park" },
                  { code: "78266", area: "Garden Ridge" },
                ].map((zip) => (
                  <div key={zip.code} className="flex items-start gap-2 p-2.5 bg-white rounded-xl" data-testid={`zip-${zip.code}`}>
                    <MapPin size={16} className="text-teal-start flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-outfit font-bold text-navy text-sm">{zip.code}</p>
                      <p className="text-text-light text-xs font-dm-sans">{zip.area}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-navy rounded-2xl p-6 text-white">
              <h3 className="font-outfit text-xl font-bold mb-3">Not in our area?</h3>
              <p className="text-white/70 font-dm-sans mb-4">
                We're expanding! Leave your info and we'll notify you when we reach your neighborhood.
              </p>
              <a
                href="https://client.sweepandgo.com/scoopt-irs0l/register"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white text-white font-bold rounded-full px-6 py-2.5 hover:bg-white/10 transition-all inline-flex items-center gap-2"
                data-testid="areas-notify-btn"
              >
                Sign Up Now! <ChevronRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="bg-off-white py-24 md:py-32" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1702151487691-0160cdcf3f19?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Professional pet waste removal"
              className="rounded-3xl shadow-xl w-full object-cover"
              style={{ maxHeight: '500px' }}
            />
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-start to-teal-end flex items-center justify-center">
                  <Star size={24} className="text-navy" />
                </div>
                <div>
                  <p className="font-outfit font-bold text-navy text-2xl">Our Goal</p>
                  <p className="text-text-light text-sm font-dm-sans">Happy Customers</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <span className="text-coral font-dm-sans font-medium tracking-wide">ABOUT SCOOPT</span>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-navy tracking-tight mt-2 mb-6">
              Your Local <span className="gradient-text">Pet Waste</span> Experts
            </h2>
            <p className="text-text-light text-lg leading-relaxed font-dm-sans mb-6">
              Founded right here in San Antonio, Scoopt was built on a simple truth: dog poop isn't going to pick itself up. That's where we come in—we handle the mess so you can actually enjoy your yard again.
            </p>
            <p className="text-text-light text-lg leading-relaxed font-dm-sans mb-6">
              We are fully insured, background-checked, and professionally committed to the job most people avoid at all costs. No gagging, no shortcuts—just a clean yard and one less thing on your plate.
            </p>
            <p className="text-text-light text-lg leading-relaxed font-dm-sans mb-8">
              We treat every yard with care and every pet like family… even if they keep leaving us "gifts."
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-start/20 flex items-center justify-center flex-shrink-0">
                  <Shield size={20} className="text-teal-start" />
                </div>
                <div>
                  <p className="font-outfit font-bold text-navy">Fully Insured</p>
                  <p className="text-text-light text-sm font-dm-sans">Peace of mind guaranteed</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-coral/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={20} className="text-coral" />
                </div>
                <div>
                  <p className="font-outfit font-bold text-navy">Satisfaction First</p>
                  <p className="text-text-light text-sm font-dm-sans">100% satisfaction guarantee</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section id="reviews" className="bg-navy py-24 md:py-32" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-coral font-dm-sans font-medium tracking-wide">OUR REVIEWS</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white tracking-tight mt-2">
            What People Are <span className="gradient-text">Saying</span>
          </h2>
          <div className="flex items-center justify-center gap-3 mt-6" data-testid="aggregate-rating">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={22} className="text-gold fill-gold" />
              ))}
            </div>
            <span className="font-outfit font-bold text-white text-xl">5.0</span>
            <span className="text-white/60 font-dm-sans">· Google Reviews</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
              data-testid={`testimonial-card-${index}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} className="text-gold fill-gold" />
                  ))}
                </div>
                <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-label="Google" data-testid={`google-icon-${index}`}>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <p className="text-white/80 font-dm-sans mb-6 leading-relaxed">"{testimonial.text}"</p>
              <div>
                <p className="font-outfit font-bold text-white">{testimonial.name}</p>
                <p className="text-white/60 text-sm font-dm-sans">{testimonial.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  return (
    <section id="faq" className="bg-off-white py-24 md:py-32" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-coral font-dm-sans font-medium tracking-wide">FAQ</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-navy tracking-tight mt-2">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-text-light text-lg mt-4 font-dm-sans">
            Everything you need to know before you sign up.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-lg p-4 sm:p-6">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="border-b border-gray-100 last:border-b-0"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-outfit font-bold text-navy text-base sm:text-lg py-5 hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-text-light font-dm-sans text-base leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="text-center mt-10">
          <p className="text-text-light font-dm-sans">
            Still have questions? Call us at{' '}
            <a href="tel:+12106429327" className="text-teal-start font-bold hover:underline" data-testid="faq-phone-link">
              (210) 642-9327
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

// Meet the Owner Section
const MeetOwnerSection = () => {
  return (
    <section className="bg-white py-24 md:py-32" data-testid="meet-owner-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-coral font-dm-sans font-medium tracking-wide">MEET THE FAMILY</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-navy tracking-tight mt-2">
            Hi, we're the family <span className="gradient-text">behind Scoopt!</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 md:gap-10 mb-12 max-w-4xl mx-auto">
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_scoopt-service/artifacts/j9xyrdg3_20260328_132917.jpg"
              alt="Jon - Owner of Scoopt"
              className="rounded-3xl shadow-xl w-full aspect-square object-cover"
              data-testid="owner-photo"
            />
          </div>
          <div className="relative">
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_43e1e462-40c0-4c1d-b4eb-f5b05f46747f/artifacts/43r4sckb_20260820_201843.webp"
              alt="The Scoopt Family"
              className="rounded-3xl shadow-xl w-full aspect-square object-cover"
              data-testid="family-photo"
            />
          </div>
        </div>

        <div className="max-w-3xl mx-auto text-center">
          <p className="text-text-light text-lg leading-relaxed font-dm-sans mb-6">
            We're a local family-owned business proud to help San Antonio dog owners enjoy cleaner, poop-free yards.
          </p>
          <p className="text-text-dark text-xl leading-relaxed font-dm-sans font-medium">
            When you choose Scoopt, you're supporting a local family—not a big corporation.
          </p>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-navy-dark py-16" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="Scoopt Logo" className="h-20 w-auto mb-4" />
            <p className="text-white/60 font-dm-sans mb-6 max-w-md">
              San Antonio's trusted pet waste removal service. We're dedicated to keeping your yard clean so you can enjoy more time with your furry friends.
            </p>
          </div>
          
          <div>
            <h4 className="font-outfit font-bold text-white mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li><a href="#services" className="text-white/60 hover:text-white transition-colors font-dm-sans">Services</a></li>
              <li><a href="#pricing" className="text-white/60 hover:text-white transition-colors font-dm-sans">Pricing</a></li>
              <li><a href="#areas" className="text-white/60 hover:text-white transition-colors font-dm-sans">Service Areas</a></li>
              <li><a href="#about" className="text-white/60 hover:text-white transition-colors font-dm-sans">About</a></li>
              <li><a href="#reviews" className="text-white/60 hover:text-white transition-colors font-dm-sans">Reviews</a></li>
              <li><a href="#faq" className="text-white/60 hover:text-white transition-colors font-dm-sans">FAQ</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-outfit font-bold text-white mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-white/60">
                <Phone size={16} className="text-teal-start" />
                <a href="tel:+12106429327" className="hover:text-white transition-colors font-dm-sans">210-642-9327</a>
              </li>
              <li className="flex items-center gap-2 text-white/60">
                <Mail size={16} className="text-teal-start" />
                <a href="mailto:Scooptup@gmail.com" className="hover:text-white transition-colors font-dm-sans">Scooptup@gmail.com</a>
              </li>
              <li className="flex items-center gap-2 text-white/60">
                <MapPin size={16} className="text-teal-start" />
                <span className="font-dm-sans">San Antonio, TX</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm font-dm-sans">
            © {new Date().getFullYear()} Scoopt. All rights reserved.
          </p>
          <p className="text-white/40 text-sm font-dm-sans">
            Proudly serving San Antonio, Texas
          </p>
        </div>

        <div className="border-t border-white/10 mt-8 pt-8 text-center" data-testid="scripture-verse">
          <p className="text-white/60 text-sm md:text-base font-dm-sans italic leading-relaxed max-w-3xl mx-auto">
            "For God so loved the world, that he gave his only begotten Son, that whosoever believeth in him should not perish, but have everlasting life."
          </p>
          <p className="text-teal-start text-xs md:text-sm font-outfit font-bold tracking-wide mt-3">
            — John 3:16
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <Navigation />
      <HeroSection />
      <ServicesSection />
      <PricingSection />
      <ServiceAreasSection />
      <AboutSection />
      <MeetOwnerSection />
      <TestimonialsSection />
      <FAQSection />
      <Footer />
    </div>
  );
}

export default App;
